import { type SerializedError } from '@reduxjs/toolkit';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { isDefined } from '@bbk/platform/query-utils';

export enum ErrorType {
  validation_error = 'validation_error',
  missing_or_invalid_email = 'missing_or_invalid_email',
}

export type APIError = {
  type: ErrorType;
  message: string;
};
export type APIV1ErrorResponse = APIError;
export type APIV2ErrorResponse = {
  metadata: { error: APIError };
};
export type APIErrorResponse = APIV1ErrorResponse | APIV2ErrorResponse;
export type RTKAPIErrorResponse = { data: APIErrorResponse; status?: number };

export const isAPIV2Error = (
  err: APIErrorResponse
): err is APIV2ErrorResponse => isDefined((err as APIV2ErrorResponse).metadata);

export const parseAPIError = (err: APIErrorResponse) =>
  isAPIV2Error(err) ? err.metadata.error : err;

const emptyApiError = (): APIError => ({
  type: ErrorType.validation_error,
  message: '',
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const messageFromUnknown = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  if (
    isRecord(value) &&
    typeof value.message === 'string' &&
    value.message.trim()
  ) {
    return value.message;
  }
  return undefined;
};

/**
 * Extracts an API error from an RTK Query rejection (or other thrown value).
 * Network errors and non-RTK throws often omit {@code data}; those return an empty
 * message so callers can fall back to a default toast string.
 */
export const parseRTKAPIError = (res: unknown): APIError => {
  if (isRecord(res) && 'data' in res && res.data != null) {
    const data = res.data;
    if (
      isRecord(data) &&
      isRecord(data.metadata) &&
      isRecord(data.metadata.error)
    ) {
      const apiError = data.metadata.error;
      const message = messageFromUnknown(apiError);
      if (message) {
        return {
          type:
            typeof apiError.type === 'string'
              ? (apiError.type as ErrorType)
              : ErrorType.validation_error,
          message,
        };
      }
    }
    if (isRecord(data)) {
      const message = messageFromUnknown(data);
      if (message) {
        return {
          type:
            typeof data.type === 'string'
              ? (data.type as ErrorType)
              : ErrorType.validation_error,
          message,
        };
      }
    }
    const stringBody = messageFromUnknown(data);
    if (stringBody) {
      return { type: ErrorType.validation_error, message: stringBody };
    }
  }

  const directMessage = messageFromUnknown(res);
  if (directMessage) {
    return { type: ErrorType.validation_error, message: directMessage };
  }

  return emptyApiError();
};

export const isRTKQErrorWithData = (
  err?: FetchBaseQueryError | SerializedError
): err is { data: string; status: number } =>
  Boolean(err && 'data' in err && typeof err.data === 'string');
